import { useState } from 'react';
import { Mail, Phone, Plus, Users, X } from 'lucide-react';
import { TipoAlvo, type PessoaLicitacaoDto } from '@nexital/shared';
import { Avatar } from '../design-system/Avatar';
import { useConfirmar } from '../design-system/Confirmacao';
import { Dialogo } from '../design-system/Dialogo';
import { useLicitacao } from './contexto';
import { Modulo } from './Modulo';
import { PontaItem, atributosAlvo } from './PontaItem';

/** Lista vazia fixa: o seletor do store não pode devolver uma lista nova a cada leitura (laço infinito no React). */
const NENHUM: never[] = [];

function CartaoPessoa({ pessoa }: { pessoa: PessoaLicitacaoDto }) {
  const remover = useLicitacao((s) => s.removerPessoa);
  const confirmar = useConfirmar();
  const removerComConfirmacao = async (p: PessoaLicitacaoDto) => {
    if (await confirmar({ titulo: 'Remover da licitação?', mensagem: `${p.nome} deixa de aparecer nas pessoas desta licitação.`, rotulo: 'Remover' })) void remover(p.uuid);
  };
  return (
    <li className="pessoa alvo-item" {...atributosAlvo(TipoAlvo.Pessoa, pessoa.uuid)}>
      <PontaItem uuid={pessoa.uuid} />
      <Avatar nome={pessoa.nome} chave={pessoa.usuarioUuid ?? pessoa.uuid} tamanho={44} />
      <div className="pessoa__dados">
        <strong>
          {pessoa.nome} {pessoa.externa && <span className="pilula pilula--informacao pessoa__externa">Contato externo</span>}
        </strong>
        <span className="pessoa__papel">{pessoa.papel}</span>
        <span className="pessoa__contatos">
          {pessoa.email && (
            <a href={`mailto:${pessoa.email}`} aria-label={`E-mail para ${pessoa.nome}`} title={pessoa.email}>
              <Mail size={16} />
            </a>
          )}
          {pessoa.telefone && (
            <a href={`tel:${pessoa.telefone}`} aria-label={`Telefone de ${pessoa.nome}`} title={pessoa.telefone}>
              <Phone size={16} />
            </a>
          )}
        </span>
      </div>
      <button type="button" className="botao-icone pessoa__remover" onClick={() => void removerComConfirmacao(pessoa)} aria-label={`Remover ${pessoa.nome} da licitação`}>
        <X size={14} />
      </button>
    </li>
  );
}

type Novo = { tipo: 'membro' | 'externo'; usuarioUuid: string; papel: string; nome: string; email: string; telefone: string };

function DialogoPessoa({ fechar }: { fechar: () => void }) {
  const membros = useLicitacao((s) => s.dados?.membrosQuadro ?? NENHUM);
  const adicionar = useLicitacao((s) => s.adicionarPessoa);
  const [n, definir] = useState<Novo>({ tipo: 'membro', usuarioUuid: membros[0]?.uuid ?? '', papel: '', nome: '', email: '', telefone: '' });
  const membro = n.tipo === 'membro';
  const valido = n.papel.trim() !== '' && (membro ? n.usuarioUuid !== '' : n.nome.trim() !== '');
  const salvar = async () => {
    const contato = { nome: n.nome.trim(), email: n.email.trim() || null, telefone: n.telefone.trim() || null };
    const gravou = await adicionar(membro ? { papel: n.papel.trim(), usuarioUuid: n.usuarioUuid } : { papel: n.papel.trim(), contato });
    if (gravou) fechar();
  };
  const campo = (chave: keyof Novo, rotulo: string, tipo = 'text') => (
    <label className="campo">
      {rotulo}
      <input type={tipo} value={n[chave]} onChange={(e) => definir({ ...n, [chave]: e.target.value })} />
    </label>
  );
  return (
    <Dialogo titulo="Adicionar pessoa" rotuloConfirmar="Adicionar" podeConfirmar={valido} aoConfirmar={() => void salvar()} aoCancelar={fechar}>
      <fieldset className="campo-opcoes">
        <legend>Quem</legend>
        <label className="campo-opcoes__opcao">
          <input type="radio" checked={membro} onChange={() => definir({ ...n, tipo: 'membro' })} /> Pessoa do quadro
        </label>
        <label className="campo-opcoes__opcao">
          <input type="radio" checked={!membro} onChange={() => definir({ ...n, tipo: 'externo' })} /> Contato externo (órgão, fornecedor)
        </label>
      </fieldset>
      {membro ? (
        <label className="campo">
          Pessoa
          <select value={n.usuarioUuid} onChange={(e) => definir({ ...n, usuarioUuid: e.target.value })}>
            {membros.map((m) => (
              <option key={m.uuid} value={m.uuid}>{m.nome}</option>
            ))}
          </select>
        </label>
      ) : (
        <>
          {campo('nome', 'Nome')}
          {campo('email', 'E-mail', 'email')}
          {campo('telefone', 'Telefone', 'tel')}
        </>
      )}
      {campo('papel', 'Papel na licitação (ex.: Documentação)')}
    </Dialogo>
  );
}

/** "Pessoas e contatos" (RQ-LIC-002): quem trabalha na licitação e os contatos externos. */
export function ModuloPessoas() {
  const pessoas = useLicitacao((s) => s.dados?.pessoas ?? NENHUM);
  const [adicionando, definirAdicionando] = useState(false);
  const acoes = (
    <button type="button" className="botao-icone" onClick={() => definirAdicionando(true)} aria-label="Adicionar pessoa">
      <Plus size={18} />
    </button>
  );
  return (
    <Modulo titulo="Pessoas e contatos" Icone={Users} cor="ciano" acoes={acoes}>
      {pessoas.length ? (
        <ul className="pessoas">
          {pessoas.map((p) => <CartaoPessoa key={p.uuid} pessoa={p} />)}
        </ul>
      ) : (
        <p className="modulo__vazio">Ninguém vinculado ainda.</p>
      )}
      {adicionando && <DialogoPessoa fechar={() => definirAdicionando(false)} />}
    </Modulo>
  );
}
